/**
 * tax-api — typed wrappers for per-tenant Tax & VAT settings + rate CRUD,
 * through the @v0y4ge/api-client `apiRaw` choke point (payments-api pattern).
 *
 * DECLARED ops served by the design-time seed until the backend ships them.
 * Tax is computed SERVER-SIDE on the amount at checkout (never the FE); this
 * surface reads + edits the configuration only. Every mutation returns the full
 * updated TaxSettings so the screen replaces its state from the response
 * (server-canonical, no optimistic local-wins).
 */
import { apiRaw } from "@v0y4ge/api-client";
import { reportOpSource } from "../../shell/opSource";

const tag = <T,>(op: string, p: Promise<T>): Promise<T> =>
  p.then((r) => { reportOpSource(op, r); return r; });
const json = (body: unknown, method: string): RequestInit => ({ method, headers: { "content-type": "application/json" }, body: JSON.stringify(body) });

export type TaxCategory = "standard" | "reduced" | "zero" | "exempt";

export interface TaxRate {
  id: string;
  name: string;
  ratePct: number;
  region: string;
  category: TaxCategory;
  isDefault: boolean;
}

export interface TaxRegistration { region: string; taxId: string; }

export interface TaxSettings {
  enabled: boolean;
  pricesIncludeTax: boolean;
  taxId: string | null;
  engine: "manual" | "stripe_tax" | null;
  currency: string;
  rates: TaxRate[];
  registrations: TaxRegistration[];
}

export type TaxRateInput = Omit<TaxRate, "id">;

/** cap: billing.read · scope: org. */
export const getTaxSettings = () =>
  tag("getTaxSettings", apiRaw<TaxSettings>("/v1/admin/tax/settings"));

/** cap: billing.write — add a rate; returns the updated settings. */
export const createTaxRate = (rate: TaxRateInput) =>
  tag("createTaxRate", apiRaw<TaxSettings>("/v1/admin/tax/rates", json(rate, "POST")));

/** cap: billing.write — edit a rate; returns the updated settings. */
export const updateTaxRate = (id: string, rate: TaxRateInput) =>
  tag("updateTaxRate", apiRaw<TaxSettings>(`/v1/admin/tax/rates/${encodeURIComponent(id)}`, json(rate, "PUT")));

/** cap: billing.write — remove a rate; returns the updated settings. */
export const deleteTaxRate = (id: string) =>
  tag("deleteTaxRate", apiRaw<TaxSettings>(`/v1/admin/tax/rates/${encodeURIComponent(id)}`, { method: "DELETE" }));

/** cap: billing.write — edit config (inclusive/exclusive, tax id); returns settings. */
export const updateTaxConfig = (patch: { pricesIncludeTax?: boolean; taxId?: string | null; enabled?: boolean }) =>
  tag("updateTaxConfig", apiRaw<TaxSettings>("/v1/admin/tax/settings", json(patch, "PUT")));
